import { DatePicker3 } from '@blueprintjs/datetime2';
import { TimeSelector } from './TimeSelector/TimeSelector';
import warningIcon from '../../assets/warning.svg';
import documentCheck from '../../assets/documentCheck.svg';

import styles from './FollowUpBoard.module.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, Divider, TextArea } from '@blueprintjs/core';
import { useCommentsState } from '../CommunicationCard/CallsAndCommentsPanel/useCommentsState';
import { useParams } from 'react-router-dom';
import {
  useApplicationDetail,
  useCommentTemplates,
} from '@axo/shared/data-access/hooks';
import { getAlreadySelectedTimes } from './TimeSelector/utils/getAlreadySelectedTimes';

type FollowUpBoardProps = {
  isWarningMessageVisible?: boolean;
  onSubmit?: () => void;
};

export const FollowUpBoard = ({
  isWarningMessageVisible = true,
  onSubmit,
}: FollowUpBoardProps) => {
  const { applicationID = '' } = useParams();
  const {
    addNewComment,
    saveComment,
    cancelComment,
    state: commentState,
  } = useCommentsState(applicationID);
  const application = useApplicationDetail(applicationID);
  const commentTemplates = useCommentTemplates(application.data?.MarketCountry);

  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const [selectedTime, setSelectedTime] = useState<string | undefined>(
    undefined
  );
  const [comment, setComment] = useState('');
  const commentStateRef = useRef(commentState);
  commentStateRef.current = commentState;

  useEffect(() => {
    addNewComment(
      commentTemplates.data?.Items?.find(
        (template) => template.Name === 'Custom'
      )
    );

    return () => {
      const newComment = commentStateRef.current.find(
        (comment) => comment.isNew
      );

      if (!newComment) return;

      cancelComment(newComment);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const _setSelectedTime = useCallback(
    (time: string) => {
      setSelectedTime(time);
    },
    [setSelectedTime]
  );

  const _setSelectedDate = useCallback(
    (date: Date | null) => {
      setSelectedDate(date ?? undefined);
    },
    [setSelectedDate]
  );

  const _onSetComment = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) => {
      const _comment = event.target.value;

      setComment(_comment);
    },
    [setComment]
  );

  const _selectedDateAsDate = useMemo(() => {
    if (selectedDate && selectedTime) {
      const [hours, minutes] = selectedTime.split(':').map(Number);
      const dateWithTime = new Date(selectedDate);
      dateWithTime.setHours(hours, minutes, 0, 0);
      return dateWithTime;
    }
    return selectedDate;
  }, [selectedDate, selectedTime]);

  const _onSubmit = useCallback(() => {
    const newComment = commentState.find((comment) => comment.isNew);

    if (!newComment) {
      console.error('No new comment found after adding a new comment');
      return;
    }

    saveComment({
      ...newComment,
      edit: {
        ...newComment.edit,
        Body: comment,
        FollowupAt: _selectedDateAsDate?.toISOString(),
      },
    });

    onSubmit?.();
  }, [comment, commentState, saveComment, _selectedDateAsDate, onSubmit]);

  const _alreadySelectedTimes = useMemo(() => {
    if (!selectedDate) return [];

    return getAlreadySelectedTimes({ commentState, selectedDate });
  }, [commentState, selectedDate]);

  const isConfirmButtonEnabled = Boolean(selectedDate && selectedTime);

  return (
    <div className={styles.setFollowUpBoard}>
      {isWarningMessageVisible && (
        <div className={styles.warningMessage}>
          <img
            src={warningIcon}
            alt="Warning"
            className={styles.warningMessage__icon}
          />
          <span className={styles.warningMessage__text}>
            You have to set up a follow up time to correctly process the
            application. You will be able to preview it in the comments section
            and get notified 15 min before.
          </span>
        </div>
      )}
      <div className={styles.dateTimeSelector}>
        <div className={styles.dateTimeSelector__item}>
          <div className={styles.dateTimeSelector__label}>
            Set follow up day:
          </div>
          <div className={styles.dateTimeSelector__card}>
            <DatePicker3 onChange={_setSelectedDate} />
          </div>
        </div>
        <div className={styles.dateTimeSelector__item}>
          <div className={styles.dateTimeSelector__label}>
            Set follow up time:
          </div>
          <div className={styles.dateTimeSelector__card}>
            <TimeSelector
              onTimeSelect={_setSelectedTime}
              selectedTime={selectedTime}
              disabledTimes={_alreadySelectedTimes}
            />
          </div>
        </div>
      </div>
      <div className={styles.customCommentSection}>
        Add custom comment:
        <TextArea
          placeholder="Write your custom comment..."
          onChange={_onSetComment}
        />
      </div>
      <Divider className={styles.divider} />
      <Button
        intent="primary"
        icon={<img src={documentCheck} alt="Document Check" />}
        className={styles.confirmButton}
        onClick={_onSubmit}
        disabled={!isConfirmButtonEnabled}
      >
        Confirm follow up
      </Button>
    </div>
  );
};
